import {
  CalendarDetailsVisibility,
  CalendarSyncEntry,
  ID,
  LinkedItemType,
  SyncMode,
  Timestamp,
} from '@griply/types';
import { resolveSyncTasksFrom } from '@griply/utils';

export enum CalendarSyncChange {
  Target = 'target',
  Calendar = 'calendar',
  Settings = 'settings',
}

export type CalendarSyncKey = {
  connectionId: ID;
  linkedItemId: ID;
};

export type CalendarSyncWrite = CalendarSyncKey & {
  linkedItemType: LinkedItemType;
  calendarId: ID;
  mode: SyncMode;
  detailsVisibility?: CalendarDetailsVisibility;
  syncTasksFrom?: Timestamp;
};

export type GetCalendarSyncWriteOpsOptions = {
  current: CalendarSyncEntry | null;
  next: Omit<CalendarSyncWrite, 'syncTasksFrom'>;
  change: CalendarSyncChange;
  referenceDate: Timestamp;
};

export type CalendarSyncWriteOps = {
  remove: CalendarSyncKey | null;
  set: CalendarSyncWrite;
};

// A sync is keyed by linkedItemId inside connection.settings.calendarSettings, so a target move
// or a hop to another connection has to delete the old entry before writing the new one. A
// re-picked target deletes unconditionally — the picker cannot report "unchanged", and one
// redundant delete is cheaper than reasoning about which half of the key moved.
const getRemoveKey = (
  current: CalendarSyncEntry | null,
  next: GetCalendarSyncWriteOpsOptions['next'],
  change: CalendarSyncChange,
): CalendarSyncKey | null => {
  if (!current) {
    return null;
  }

  const key = {
    connectionId: current.connectionId,
    linkedItemId: current.linkedItemId,
  };

  if (change === CalendarSyncChange.Target) {
    return key;
  }

  if (change === CalendarSyncChange.Calendar) {
    return next.connectionId === current.connectionId ? null : key;
  }

  return null;
};

export const getCalendarSyncWriteOps = ({
  current,
  next,
  change,
  referenceDate,
}: GetCalendarSyncWriteOpsOptions): CalendarSyncWriteOps => ({
  remove: getRemoveKey(current, next, change),
  set: {
    ...next,
    syncTasksFrom: resolveSyncTasksFrom(
      next.linkedItemType,
      current?.syncTasksFrom,
      referenceDate,
    ),
  },
});
